import {
  type DeploySourceRow,
  fetchApps,
  selectSourcesForTableSearch,
} from "@app/deploy";
import { fetchDeployments } from "@app/deployment";
import { useSelector } from "@app/react";
import { sourceDetailUrl, sourcesSetupUrl } from "@app/routes";
import { fetchSources } from "@app/source";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "starfx/react";
import { usePaginate, useSyncedQueryParam } from "../hooks";
import { AppSidebarLayout } from "../layouts";
import {
  ButtonAnyOwner,
  CopyText,
  DescBar,
  EmptyTr,
  FilterBar,
  GitRef,
  Group,
  IconChevronDown,
  IconInfo,
  IconPlusCircle,
  InputSearch,
  LoadingBar,
  PaginateBar,
  SourceLogo,
  TBody,
  THead,
  Table,
  Td,
  Th,
  TitleBar,
  Tooltip,
  Tr,
  tokens,
} from "../shared";

function SourceListRow({ source }: { source: DeploySourceRow }) {
  const liveCommits = source.liveCommits.slice(0, 7);
  const extraLiveCommits = source.liveCommits.length - liveCommits.length;

  return (
    <Tr key={source.id}>
      <Td>
        <Link to={sourceDetailUrl(source.id)} className="flex items-center">
          <SourceLogo
            source={source}
            className="w-[32px] h-[32px] mr-2 align-middle"
          />
          <p className={`${tokens.type["table link"]} leading-8`}>
            {source.displayName}
          </p>
        </Link>
      </Td>
      <Td>
        <CopyText text={source.url}>{source.url}</CopyText>
      </Td>
      <Td>
        {liveCommits.length === 0 ? <em>No commit information</em> : null}

        <Group
          variant="horizontal"
          size="xs"
          className="items-center min-w-[150px]"
        >
          {liveCommits.map((liveCommit) => (
            <Tooltip
              key={liveCommit.sha}
              fluid
              theme="light"
              placement="top"
              text={
                <GitRef
                  gitRef={liveCommit.ref}
                  commitSha={liveCommit.sha}
                  commitUrl={liveCommit.url}
                />
              }
            >
              <Link
                to="#"
                className="block bg-gray-300 h-[16px] w-[6px] hover:bg-indigo rounded-md"
              />
            </Tooltip>
          ))}
          {extraLiveCommits ? <p>+{extraLiveCommits}</p> : null}
        </Group>
      </Td>
      <Td variant="center" className="center items-center justify-center">
        <div className="text-center">{source.apps.length}</div>
      </Td>
    </Tr>
  );
}

const SortIcon = () => (
  <div className="inline-block">
    <IconChevronDown
      variant="sm"
      className="top-1 -ml-1 relative group-hover:opacity-100 opacity-50"
    />
  </div>
);

export function SourcesPage() {
  const { isLoading: appsLoading } = useQuery(fetchApps());
  const { isLoading: deploymentsLoading } = useQuery(fetchDeployments());
  const { isLoading: sourcesLoading } = useQuery(fetchSources());
  const isLoading = appsLoading || deploymentsLoading || sourcesLoading;

  const { search, onChange } = useSyncedQueryParam();
  const [params, setParams] = useSearchParams();
  const defaultSortBy =
    (params.get("sortBy") as keyof DeploySourceRow) || "handle";
  const defaultSortDir = (params.get("asc") as "asc" | "desc") || "asc";
  const [sortBy, setSortBy] = useState<typeof defaultSortBy>(defaultSortBy);
  const [sortDir, setSortDir] = useState<typeof defaultSortDir>(defaultSortDir);
  const onSort = (key: keyof DeploySourceRow) => {
    const newSortBy = key;
    const newSortDir = sortDir === "asc" ? "desc" : "asc";

    if (newSortBy === sortBy) {
      setSortDir(newSortDir);
    } else {
      setSortBy(newSortBy);
      setSortDir(defaultSortDir);
    }

    setParams(
      (prev) => {
        prev.set("sortBy", newSortBy);
        prev.set("sortDir", newSortDir);
        return prev;
      },
      { replace: true },
    );
  };

  const sources = useSelector((s) =>
    selectSourcesForTableSearch(s, {
      search,
      sortBy,
      sortDir,
    }),
  );
  const paginated = usePaginate(sources);

  // If there are no sources to display, redirect the user to the setup page.
  const navigate = useNavigate();
  if (!isLoading && search === "" && sources.length === 0) {
    navigate(sourcesSetupUrl());
  }

  return (
    <AppSidebarLayout>
      <Group>
        <Group size="sm">
          <Group variant="horizontal" className="items-center justify-between">
            <TitleBar description="Sources connect apps with code repositories to show you what's deployed where.">
              Sources
            </TitleBar>
            <ButtonAnyOwner
              onClick={() => navigate(sourcesSetupUrl())}
              tooltipProps={{ placement: "left" }}
            >
              <IconPlusCircle variant="sm" className="mr-2" />
              New Source
            </ButtonAnyOwner>
          </Group>

          <FilterBar>
            <Group
              variant="horizontal"
              size="lg"
              className="items-center justify-between"
            >
              <Group variant="horizontal" size="lg" className="items-center">
                <DescBar>{paginated.totalItems} Apps</DescBar>
                <PaginateBar {...paginated} />
              </Group>
              <InputSearch
                placeholder="Search..."
                search={search}
                onChange={onChange}
              />
            </Group>
            <LoadingBar isLoading={isLoading} />
          </FilterBar>
        </Group>

        <Table>
          <THead>
            <Th
              className="cursor-pointer hover:text-black group"
              onClick={() => onSort("displayName")}
            >
              Name <SortIcon />
            </Th>
            <Th>Source URL</Th>
            <Th
              className="cursor-pointer hover:text-black group"
              onClick={() => onSort("liveCommits")}
            >
              Live Commits
              <div className="inline-block mx-2">
                <Tooltip
                  placement="top"
                  text="The number of unique commits from this source currently running in your infrastructure."
                >
                  <IconInfo
                    variant="sm"
                    className="top-1 -ml-1 relative group-hover:opacity-100 opacity-50"
                  />
                </Tooltip>
              </div>
              <SortIcon />
            </Th>
            <Th
              variant="center"
              className="cursor-pointer hover:text-black group"
              onClick={() => onSort("apps")}
            >
              Apps <SortIcon />
            </Th>
          </THead>

          <TBody>
            {paginated.data.length === 0 ? <EmptyTr colSpan={3} /> : null}
            {paginated.data.map((source) => (
              <SourceListRow source={source} key={source.id} />
            ))}
          </TBody>
        </Table>
        {paginated.totalPages > 1 && <PaginateBar {...paginated} />}
      </Group>
    </AppSidebarLayout>
  );
}
